import React, { useState } from 'react';
import { PaystackButton } from '../components/PaystackButton';
import { Check, Mail, Users, Target, TrendingUp, Award, Clock, MapPin, Globe, Laptop, Wifi, CheckCircle, Star, Quote } from 'lucide-react';
export function VirtualSalesPage() {
  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const handleEnrollClick = () => {
    setShowEmailInput(true);
  };
  const handlePaymentSuccess = (reference: string) => {
    alert(`Payment successful! Reference: ${reference}. We'll send you enrollment details shortly.`);
    setEmail('');
    setShowEmailInput(false);
  };
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1624">
      <main className="flex-grow pt-20" data-id="element-1625">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8" data-id="element-1626">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1627">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-1628">
              {/* Hero */}
              <section className="mb-12" data-id="element-1629">
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4" data-id="element-1630">
                  Virtual Sales Professionals Training
                </h1>
                <p className="text-2xl md:text-3xl font-semibold text-blue-600 mb-6" data-id="element-1631">
                  Master modern sales skills and transform your career
                </p>

                <div className="rounded-2xl overflow-hidden mb-6 shadow-xl" data-id="element-1632">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aHky176nDshKcDhStEuadp/IMG_20251023_125324.jpg" alt="Virtual Sales Training" className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500" data-id="element-1633" />
                </div>
              </section>

              {/* About */}
              <section className="mb-12" data-id="element-1634">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1635">
                  About the Course
                </h2>
                <div className="prose prose-lg max-w-none" data-id="element-1636">
                  <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1637">
                    Sales is not just a function — it's how value gets created,
                    communicated and delivered. The Virtual Sales Professionals
                    course from SkillzHQ Academy is an 8-week online program
                    that teaches practical sales know-how you can use right
                    away.
                  </p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1638">
                    Through live masterclasses, realistic case work, role-play
                    and tools practice, you'll learn how to find prospects,
                    build trusting relationships and close deals that last — all
                    with a focus on what works in African markets.
                  </p>
                </div>
              </section>

              {/* Who This Is For */}
              <section className="mb-12" data-id="element-1639">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1640">
                  Who This Course Is For
                </h2>
                <div className="space-y-4" data-id="element-1641">
                  {['Founders & entrepreneurs who need a dependable sales process to grow revenue and scale predictably', 'Early-career sales professionals who want modern techniques that actually move numbers', 'Career switchers looking to break into sales with real skills employers recognise', "HR & learning leads seeking a practical program to boost their teams' selling capability"].map((item, index) => <div key={index} className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg" data-id="element-1642">
                      <Users className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-1643" />
                      <p className="text-gray-700" data-id="element-1644">{item}</p>
                    </div>)}
                </div>
              </section>

              {/* What You'll Learn */}
              <section className="mb-12" data-id="element-1645">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1646">
                  What You'll Learn
                </h2>

                <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 mb-6" data-id="element-1647">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1648">
                    Core Sales Skills
                  </h3>
                  <div className="space-y-4" data-id="element-1649">
                    {['Reframe sales as value creation: identify real customer problems and propose solutions', 'Buyer psychology: how decisions are made and how to influence ethically', 'Prospecting & lead generation methods that work in local markets', 'Pitching and negotiation techniques to move deals forward', 'Closing strategies and post-sale follow-up that turn customers into advocates'].map((skill, index) => <div key={index} className="flex items-start gap-3" data-id="element-1650">
                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-1651" />
                        <p className="text-gray-700" data-id="element-1652">{skill}</p>
                      </div>)}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8" data-id="element-1653">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1654">
                    Career Growth & Tools
                  </h3>
                  <div className="space-y-4" data-id="element-1655">
                    {['Hands-on use of modern CRMs (HubSpot, Salesforce basics) to track pipeline and performance', 'Building data-driven, personalized sales plans', 'Storytelling and presentation skills for pitches and stakeholder updates', 'CV polish and interview preparation specific to sales roles', "Access routes to internships and entry roles via SkillzHQ's network"].map((skill, index) => <div key={index} className="flex items-start gap-3" data-id="element-1656">
                        <Award className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" data-id="element-1657" />
                        <p className="text-gray-700" data-id="element-1658">{skill}</p>
                      </div>)}
                  </div>
                </div>
              </section>

              {/* Course Details */}
              <section className="mb-12" data-id="element-1659">
                <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-1660">
                  Course Details
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-1661">
                  <div className="bg-white rounded-xl p-6 border-2 border-blue-100" data-id="element-1662">
                    <Clock className="w-8 h-8 text-blue-600 mb-4" data-id="element-1663" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1664">Duration</h3>
                    <p className="text-gray-700" data-id="element-1665">8 weeks</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border-2 border-green-100" data-id="element-1666">
                    <MapPin className="w-8 h-8 text-green-600 mb-4" data-id="element-1667" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1668">Format</h3>
                    <p className="text-gray-700" data-id="element-1669">
                      Self-paced with weekly cohort checkpoints and live
                      masterclasses
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border-2 border-blue-100" data-id="element-1670">
                    <Globe className="w-8 h-8 text-blue-600 mb-4" data-id="element-1671" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1672">Delivery</h3>
                    <p className="text-gray-700" data-id="element-1673">
                      Fully virtual — recorded lessons + live sessions +
                      practical assignments
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border-2 border-green-100" data-id="element-1674">
                    <Users className="w-8 h-8 text-green-600 mb-4" data-id="element-1675" />
                    <h3 className="font-bold text-gray-900 mb-2" data-id="element-1676">
                      Cohort Size
                    </h3>
                    <p className="text-gray-700" data-id="element-1677">
                      Limited to ensure personalised feedback
                    </p>
                  </div>
                </div>

                {/* Requirements */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200" data-id="element-1678">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1679">
                    Requirements
                  </h3>
                  <div className="space-y-4" data-id="element-1680">
                    <div className="flex items-start gap-3" data-id="element-1681">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-1682" />
                      <p className="text-gray-700" data-id="element-1683">
                        Dedicated time: plan for 4–6 hours per week (lessons,
                        practice, assignments)
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-1684">
                      <Laptop className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-1685" />
                      <p className="text-gray-700" data-id="element-1686">
                        Device: PC or laptop recommended (Windows 10+/macOS
                        latest); smartphones may be used for some activities
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-1687">
                      <Wifi className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-1688" />
                      <p className="text-gray-700" data-id="element-1689">
                        Reliable internet connection for live sessions
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-1690">
                      <Globe className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-1691" />
                      <p className="text-gray-700" data-id="element-1692">
                        Compatible browsers: latest Google Chrome, Firefox,
                        Edge, or Safari
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Outcomes */}
              <section className="mb-12" data-id="element-1693">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1694">
                  Outcomes & Benefits
                </h2>
                <p className="text-gray-700 mb-6" data-id="element-1695">
                  By the end of the program you will:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1696">
                  {[{
                  icon: Award,
                  text: 'Earn a recognised Sales Professional Certificate from SkillzHQ Academy'
                }, {
                  icon: Target,
                  text: 'Have a practical sales playbook and personalized plan you can implement immediately'
                }, {
                  icon: TrendingUp,
                  text: 'Gain hands-on exposure to CRM tools and live role-play feedback'
                }, {
                  icon: Users,
                  text: 'Receive CV support and direct routes to internships or entry-level roles via our partner network'
                }].map((item, index) => {
                  const Icon = item.icon;
                  return <div key={index} className="bg-white rounded-xl p-6 border-2 border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300" data-id="element-1697">
                        <Icon className="w-8 h-8 text-blue-600 mb-4" data-id="element-1698" />
                        <p className="text-gray-700" data-id="element-1699">{item.text}</p>
                      </div>;
                })}
                </div>
              </section>

              {/* Instructor */}
              <section className="mb-12" data-id="element-1700">
                <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-1701">
                  Your Instructor
                </h2>
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100" data-id="element-1702">
                  <div className="flex items-start gap-6" data-id="element-1703">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9FxdUVvXCZYa3KKcZPsPKK/IMG_0273.png" alt="Ms. Precious Imuwahen Ogbomo" className="w-32 h-32 rounded-2xl object-cover" data-id="element-1704" />
                    <div data-id="element-1705">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1706">
                        Ms. Precious Imuwahen Ogbomo
                      </h3>
                      <p className="text-gray-700 leading-relaxed" data-id="element-1707">
                        A seasoned sales and learning strategist with 15+ years'
                        experience driving growth for global brands like
                        ExxonMobil, Nestlé, and Guinness Nigeria. She has
                        trained over 15,000 professionals across Africa,
                        designed the Jobberman Soft Skills Curriculum that
                        reached 5 million young people, and continues to equip
                        individuals with practical tools to sell, lead, and
                        succeed in today's market.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* FAQs */}
              <section className="mb-12" data-id="element-1708">
                <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-1709">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4" data-id="element-1710">
                  {[{
                  q: 'Is this course for beginners?',
                  a: 'Yes. The course starts with fundamentals and builds quickly into applied techniques suitable for both newcomers and people already in sales.'
                }, {
                  q: 'What if I miss a live session?',
                  a: "Live sessions are recorded. You'll get access to the replay and can submit any assignment or question afterward for feedback."
                }, {
                  q: 'Will I get a certificate?',
                  a: 'Yes. Participants who complete the assessments and capstone receive a SkillzHQ Sales Professional Certificate.'
                }, {
                  q: 'Are there internship or recruitment opportunities?',
                  a: 'We share top candidates with partner organisations and support learners with recruitment-ready CVs and interview prep.'
                }, {
                  q: 'Can my organisation buy several seats?',
                  a: 'Absolutely. We have special pricing for groups and organisations — contact myskillzhq@gmail.com to discuss.'
                }].map((faq, index) => <details key={index} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-blue-200 transition-colors duration-300 group" data-id="element-1711">
                      <summary className="font-bold text-gray-900 cursor-pointer list-none flex items-center justify-between" data-id="element-1712">
                        <span data-id="element-1713">{faq.q}</span>
                        <span className="text-blue-600 group-open:rotate-180 transition-transform duration-300" data-id="element-1714">
                          ▼
                        </span>
                      </summary>
                      <p className="text-gray-700 mt-4 leading-relaxed" data-id="element-1715">
                        {faq.a}
                      </p>
                    </details>)}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1" data-id="element-1716">
              <div className="lg:sticky lg:top-24" data-id="element-1717">
                <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-100 p-8" data-id="element-1718">
                  <div className="mb-6" data-id="element-1719">
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-1720">
                      <span className="text-4xl font-bold text-gray-900" data-id="element-1721">
                        ₦150,000
                      </span>
                      <span className="text-gray-600" data-id="element-1722">/ $100</span>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-1723">
                      One-time payment for full course access
                    </p>
                  </div>

                  <div className="space-y-4 mb-8" data-id="element-1724">
                    {['8-week comprehensive program', 'Live masterclasses with expert instructor', 'Hands-on CRM tools practice', 'Sales playbook and templates', 'Professional certificate', 'Career support and job placement'].map((item, index) => <div key={index} className="flex items-start gap-3" data-id="element-1725">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1726" />
                        <span className="text-gray-700" data-id="element-1727">{item}</span>
                      </div>)}
                  </div>

                  {!showEmailInput ? <button onClick={handleEnrollClick} className="w-full bg-blue-600 text-white font-semibold py-4 px-6 rounded-full hover:bg-blue-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 transform" data-id="element-1728">
                      Enroll Now - Limited Slots
                    </button> : <div className="space-y-4" data-id="element-1729">
                      <div data-id="element-1730">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1731">
                          Enter your email to proceed
                        </label>
                        <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@example.com" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200" required data-id="element-1732" />
                      </div>
                      <PaystackButton amount={150000} email={email} onSuccess={handlePaymentSuccess} variant="primary" fullWidth data-id="element-1733">
                        Pay ₦150,000
                      </PaystackButton>
                      <button onClick={() => setShowEmailInput(false)} className="w-full text-gray-600 text-sm hover:text-gray-800 transition-colors duration-200" data-id="element-1734">
                        Cancel
                      </button>
                    </div>}

                  <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-1735">
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-1736">
                      <p className="text-sm text-green-900 font-medium mb-2" data-id="element-1737">
                        🎓 Special Group Rates Available
                      </p>
                      <p className="text-xs text-green-800 mb-3" data-id="element-1738">
                        Organizations interested in bulk purchase can get
                        tailored pricing.
                      </p>
                      <a href="mailto:myskillzhq@gmail.com" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200" data-id="element-1739">
                        <Mail className="w-4 h-4" data-id="element-1740" />
                        myskillzhq@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>;
}